import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  IconCalendar,
  IconChevronLeft,
  IconChevronRight,
  IconChevronsLeft,
  IconChevronsRight,
} from '@tabler/icons-react';
import { useDropdownDismiss } from './useDropdownDismiss';
import './DateField.css';

/**
 * Custom date picker (C2). Replaces native <input type="date"> app-wide. The
 * value crosses the boundary as an ISO `yyyy-MM-dd` string (what every existing
 * date input uses), displayed as `dd/MM/yyyy`. The trigger keeps the floating
 * label + border treatment; the calendar portals to <body> and dismisses on
 * outside-click / Escape via the shared dropdown hook.
 *
 * Native Date math (no date-fns dependency) — all local-time so the calendar
 * day never shifts across the UTC boundary.
 *
 * The field is a real text input, so it takes Tab focus in form order and a
 * date can be typed (day first — `14/11/2026`, `14-11-26`, `14112026`, or ISO)
 * and committed on Enter or blur. An unparseable entry reverts rather than
 * clearing; an empty one clears. ArrowDown opens the calendar. Mouse use is
 * unchanged: a click opens the calendar, and picking a day returns focus to
 * the field so Tab carries on from it.
 */
interface DateFieldProps {
  label: string;
  /** ISO `yyyy-MM-dd` (or null/empty). */
  value: string | null;
  onChange: (iso: string | null) => void;
  disabled?: boolean;
  /** Red border + red helper text (validation display; logic stays in the host). */
  error?: boolean;
  /** Small caption shown beneath the field. */
  helperText?: string;
}

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const pad = (n: number) => String(n).padStart(2, '0');
const toISO = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const formatDisplay = (d: Date) => `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
const formatMonthYear = (d: Date) => `${MONTHS[d.getMonth()]} ${d.getFullYear()}`;

const parseISO = (iso: string | null): Date | null => {
  if (!iso) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
};

/**
 * Read a typed date, day first (en-AU). Accepts `d/m/yyyy` with `/`, `-` or `.`
 * separators, a two-digit year (20xx), eight digits `ddmmyyyy`, and ISO
 * `yyyy-mm-dd`. Returns ISO `yyyy-MM-dd`, or null when it isn't a real date —
 * 31/02 is refused rather than rolled into March.
 */
export const parseTypedDate = (text: string): string | null => {
  const t = text.trim();
  let y: number;
  let m: number;
  let d: number;
  let match = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(t);
  if (match) {
    [y, m, d] = [Number(match[1]), Number(match[2]), Number(match[3])];
  } else if ((match = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2}|\d{4})$/.exec(t))) {
    [d, m, y] = [Number(match[1]), Number(match[2]), Number(match[3])];
    if (match[3].length === 2) y += 2000;
  } else if ((match = /^(\d{2})(\d{2})(\d{4})$/.exec(t))) {
    [d, m, y] = [Number(match[1]), Number(match[2]), Number(match[3])];
  } else {
    return null;
  }
  const date = new Date(y, m - 1, d);
  if (date.getFullYear() !== y || date.getMonth() !== m - 1 || date.getDate() !== d) return null;
  return toISO(date);
};

const isSameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const isSameMonth = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();
const isToday = (d: Date) => isSameDay(d, new Date());
const addMonths = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth() + n, 1);
const addYears = (d: Date, n: number) => new Date(d.getFullYear() + n, d.getMonth(), 1);

/** Monday-first 6-week (42-cell) grid covering `view`'s month. */
const buildCalendarDays = (view: Date): Date[] => {
  const first = new Date(view.getFullYear(), view.getMonth(), 1);
  const offset = (first.getDay() + 6) % 7; // Mon = 0
  return Array.from(
    { length: 42 },
    (_, i) => new Date(first.getFullYear(), first.getMonth(), 1 - offset + i),
  );
};

export const DateField: React.FC<DateFieldProps> = ({
  label,
  value,
  onChange,
  disabled,
  error,
  helperText,
}) => {
  const [open, setOpen] = useState(false);
  const selected = parseISO(value);
  const display = selected ? formatDisplay(selected) : '';
  // The text being typed; null while not editing, so the field shows `value`.
  const [draft, setDraft] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [viewDate, setViewDate] = useState<Date>(selected ?? new Date());
  const triggerRef = useRef<HTMLDivElement>(null);
  const pickerRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<React.CSSProperties>({ position: 'fixed', visibility: 'hidden' });

  const close = useCallback(() => setOpen(false), []);
  useDropdownDismiss(pickerRef, triggerRef, close, open);

  const openPicker = () => {
    if (disabled) return;
    setViewDate(selected ?? new Date());
    setOpen(true);
  };

  /** Commit what was typed. Unchanged text commits nothing, so tabbing
   *  through a field never marks a form as edited. */
  const commitDraft = () => {
    if (draft === null) return;
    const text = draft.trim();
    setDraft(null);
    if (text === display) return;
    if (text === '') {
      if (value) onChange(null);
      return;
    }
    const iso = parseTypedDate(text);
    if (iso && iso !== value) onChange(iso);
  };

  /** A calendar pick: set it, close, and put focus back for Tab. */
  const pick = (iso: string | null) => {
    setDraft(null);
    onChange(iso);
    setOpen(false);
    inputRef.current?.focus();
  };

  // Portalled: position below the trigger, flipping above when space is tight.
  useLayoutEffect(() => {
    if (!open) return;
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const openAbove = window.innerHeight - rect.bottom < 320;
    setPosition(
      openAbove
        ? { position: 'fixed', bottom: window.innerHeight - rect.top + 4, left: rect.left }
        : { position: 'fixed', top: rect.bottom + 4, left: rect.left },
    );
  }, [open]);

  const calendarDays = useMemo(() => buildCalendarDays(viewDate), [viewDate]);

  return (
    <div className="date-field" ref={triggerRef}>
      <span className="date-field__label">{label}</span>
      <div
        className={`date-field__trigger ${error ? 'date-field__trigger--error' : ''}`}
        onClick={openPicker}
      >
        <input
          ref={inputRef}
          className="date-field__value date-field__input"
          aria-label={label}
          placeholder="dd/mm/yyyy"
          autoComplete="off"
          value={draft ?? display}
          disabled={disabled}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commitDraft}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commitDraft();
            } else if (e.key === 'ArrowDown') {
              e.preventDefault();
              openPicker();
            } else if (e.key === 'Escape' && draft !== null) {
              setDraft(null);
            }
          }}
        />
        <button
          type="button"
          className="date-field__icon-btn"
          aria-label="Open calendar"
          tabIndex={-1}
          disabled={disabled}
          onClick={(e) => {
            e.stopPropagation();
            if (open) setOpen(false);
            else openPicker();
          }}
        >
          <IconCalendar size={13} />
        </button>
      </div>

      {helperText && (
        <div className={`date-field__helper ${error ? 'date-field__helper--error' : ''}`}>
          {helperText}
        </div>
      )}

      {open &&
        createPortal(
          <div className="date-picker" ref={pickerRef} style={position}>
            <div className="date-picker__header">
              <span className="date-picker__month-label">{formatMonthYear(viewDate)}</span>
              <button
                type="button"
                className="date-picker__nav-btn"
                aria-label="Previous year"
                onClick={() => setViewDate(addYears(viewDate, -1))}
              >
                <IconChevronsLeft size={13} />
              </button>
              <button
                type="button"
                className="date-picker__nav-btn"
                aria-label="Previous month"
                onClick={() => setViewDate(addMonths(viewDate, -1))}
              >
                <IconChevronLeft size={13} />
              </button>
              <button
                type="button"
                className="date-picker__nav-btn"
                aria-label="Next month"
                onClick={() => setViewDate(addMonths(viewDate, 1))}
              >
                <IconChevronRight size={13} />
              </button>
              <button
                type="button"
                className="date-picker__nav-btn"
                aria-label="Next year"
                onClick={() => setViewDate(addYears(viewDate, 1))}
              >
                <IconChevronsRight size={13} />
              </button>
            </div>

            <div className="date-picker__body">
              <div className="date-picker__grid">
                {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((d, i) => (
                  <div key={i} className="date-picker__doh">{d}</div>
                ))}
                {calendarDays.map((day, i) => {
                  const inMonth = isSameMonth(day, viewDate);
                  const cls = [
                    'date-picker__day',
                    !inMonth ? 'date-picker__day--other' : '',
                    isToday(day) ? 'date-picker__day--today' : '',
                    selected && isSameDay(day, selected) ? 'date-picker__day--selected' : '',
                  ].filter(Boolean).join(' ');
                  return (
                    <div
                      key={i}
                      className={cls}
                      onClick={() => {
                        if (!inMonth) return;
                        pick(toISO(day));
                      }}
                    >
                      {day.getDate()}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="date-picker__footer">
              <button
                type="button"
                className="date-picker__clear-btn"
                onClick={() => pick(null)}
              >
                Clear
              </button>
              <button
                type="button"
                className="date-picker__today-btn"
                onClick={() => {
                  const today = new Date();
                  setViewDate(today);
                  pick(toISO(today));
                }}
              >
                Today
              </button>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};
